"use client";

import Image from "next/image";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedText } from "./animated-text";
import Section, { SectionVariant } from "./section";
import { Wrench } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AccordionContent, LocaleCode } from "@/types/sections";
import { pickLocalizedList, pickLocalizedString } from "@/lib/locale";
import { resolveIcon } from "@/lib/icon-map";
import { sanitizeImageUrl, fallbackImage } from "@/lib/image-safety";

/* ---------- data ---------- */

type AccordionItemData = {
  id: number;
  title: string;
  imageUrl: string;
  color: string;
  paragraphs: string[];
};

type Props = {
  content: AccordionContent;
  locale: LocaleCode;
};

/* ---------- helpers ---------- */

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false); // same on server and on first client render

  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange(); // set initial value after mount
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/* ---------- item ---------- */

type AccordionItemProps = {
  item: AccordionItemData;
  isActive: boolean;
  onActivate: () => void;
  index: number;
  isDesktop: boolean;
};

const AccordionItem: React.FC<AccordionItemProps> = ({
  item,
  isActive,
  onActivate,
  index,
  isDesktop,
}) => {
  const [imgErrored, setImgErrored] = useState(false);
  // Responsive sizing:
  // - Desktop (md+): width expands, fixed min-height
  // - Mobile: full width; height collapses to a small header row when inactive
  const collapsedRowH = 72; // px
  const expandedRowH = 440; // px

  return (
    <motion.div
      className="relative rounded-2xl overflow-hidden cursor-pointer focus:outline-none"
      onPointerEnter={isDesktop ? onActivate : undefined}
      onFocus={onActivate}
      onClick={onActivate}
      tabIndex={0}
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.12, duration: 0.4 }}
      style={{
        width: isDesktop ? (isActive ? "100%" : 80) : "100%",
        minHeight: isDesktop ? 450 : isActive ? expandedRowH : collapsedRowH,
        transition: "width 0.45s ease-in-out, min-height 0.35s ease",
      }}
      aria-pressed={isActive}
      role="button"
    >
      <Image
        src={imgErrored ? fallbackImage() : sanitizeImageUrl(item.imageUrl)}
        alt=""
        width={1600}
        height={900}
        className="absolute inset-0 w-full h-full object-cover"
        priority={index === 0}
        sizes="(min-width: 1024px) 70vw, 100vw"
        loading={index === 0 ? "eager" : "lazy"}
        quality={80}
        onError={() => setImgErrored(true)}
      />

      <div
        className="absolute inset-0"
        style={{
          backgroundColor: isActive ? "rgba(0, 0, 0, 0.45)" : item.color,
          transition: "background-color 0.3s ease",
        }}
      />

      {/* Title */}
      <motion.h2
        className="absolute text-white font-semibold whitespace-nowrap text-xl sm:text-2xl"
        animate={
          isDesktop
            ? // desktop positioning (like your original)
              isActive
              ? { top: 24, left: "50%", x: "-50%", rotate: 0 }
              : { top: "80%", left: "50%", x: "-50%", y: "-50%", rotate: 90 }
            : // mobile: keep it as a left-aligned row header
            isActive
            ? { top: 16, left: 16, rotate: 0 }
            : { top: 16, left: 16, rotate: 0 }
        }
        transition={{ duration: 0.35 }}
      >
        {item.title}
      </motion.h2>

      {/* Content */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            key="content"
            className={`absolute text-white ${
              isDesktop
                ? "top-20 left-1/2 -translate-x-1/2 w-[90%] max-h-[80%] overflow-y-auto"
                : "top-16 left-0 right-0 mx-auto px-4 pb-4 max-h-[calc(100%-64px)] overflow-y-auto"
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {item.paragraphs.map((para, idx) => (
              <AnimatedText
                key={idx}
                text={para}
                initialPause={idx * 0.1}
                className="text-base sm:text-lg"
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ---------- root ---------- */

export const LandingAccordionItem: React.FC<Props> = ({ content, locale }) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const timerRef = React.useRef<number | null>(null);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const SectionIcon = resolveIcon(content.iconKey, Wrench);
  const eyebrow = pickLocalizedString(
    content.eyebrow,
    locale,
    "Ce que nous offrons exclusivement à nos membres"
  );
  const title = pickLocalizedString(content.title, locale, "Nos Services");
  const subtitle = pickLocalizedString(content.subtitle, locale);
  const accordionItems = (content.items ?? []).map((item, index) => ({
    id: index + 1,
    title: pickLocalizedString(item.title, locale),
    imageUrl: item.imageUrl,
    color: item.color,
    paragraphs: pickLocalizedList(item.paragraphs, locale),
  }));
  const itemsCount = accordionItems.length;

  const startAutoplay = React.useCallback(() => {
    if (itemsCount <= 1 || timerRef.current != null) return;
    timerRef.current = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % itemsCount);
    }, 4000);
  }, [itemsCount]);

  const stopAutoplay = React.useCallback(() => {
    if (timerRef.current != null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (itemsCount <= 1) return;
    startAutoplay();
    return () => stopAutoplay();
  }, [startAutoplay, stopAutoplay, itemsCount]);

  // Pause on interaction
  const handleStripEnter = () => stopAutoplay();
  const handleStripLeave = () => startAutoplay();

  const handleItemActivate = (index: number) => {
    if (itemsCount === 0) return;
    stopAutoplay();
    setActiveIndex(index);
  };

  useEffect(() => {
    if (activeIndex >= itemsCount && itemsCount > 0) {
      setActiveIndex(0);
    }
  }, [itemsCount, activeIndex]);

  const isMobile = useIsMobile();
  const [sectionVariant, setSectionVariant] = useState<SectionVariant>("center");
  useEffect(() => {
    // Transform state whenever breakpoint changes
    if (isMobile) {
      setSectionVariant("left");
    } else {
      setSectionVariant("center");
    }
  }, [isMobile]);

  if (itemsCount === 0) {
    return (
      <Section
        id="services"
        title={title}
        eyebrow={eyebrow}
        variant={sectionVariant}
        subtitle={subtitle}
        Icon={SectionIcon}
      >
        <p className="text-sm text-muted-foreground">
          Contenu à venir. Ajoutez des services dans l’admin pour alimenter cette section.
        </p>
      </Section>
    );
  }

  return (
    <Section
      id="services"
      title={title}
      eyebrow={eyebrow}
      variant={sectionVariant}
      subtitle={subtitle}
      Icon={SectionIcon}
    >
      <div className="font-sans">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="w-full">
            <div
              className={
                // Mobile: vertical stack; Desktop: horizontal strip
                "flex md:flex-row flex-col items-stretch justify-center md:items-center " +
                "gap-3 md:gap-4 md:overflow-x-auto overflow-y-visible p-3 md:p-4"
              }
              onPointerEnter={handleStripEnter}
              onPointerLeave={handleStripLeave}
              onFocusCapture={handleStripEnter}
              onBlurCapture={handleStripLeave}
            >
              {accordionItems.map((item, idx) => (
                <AccordionItem
                  key={item.id}
                  item={item}
                  index={idx}
                  isActive={idx === activeIndex}
                  onActivate={() => handleItemActivate(idx)}
                  isDesktop={isDesktop}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
